// src/components/Leaderboard/Leaderboard.js

import React from 'react';
import { Link } from 'react-router-dom';
import './Leaderboard.css';
import profilePic from "../../assets/images/profile_image.png";
import Button from '../../components/Button/Button3';
import { buildPlayerPath, buildProfilePath, isOwnPlayerPath } from '../../utils/appRoutes';

const Leaderboard = ({
  leaderboardData,
  currentUserId,
  viewerUserId = null,
  compactNames = false,
  currentPage,
  totalPages,
  onPrevPage,
  onNextPage,
  onSearchChange,
  searchTerm,
  isLoading = false,
  noResultsMessage = 'No users found.',
}) => {

  // Handler for local input change -> pass to parent
  const handleSearchInput = (e) => {
    onSearchChange(e.target.value);
  };

  const formatDisplayName = (user) => {
    const firstName = String(user?.first_name || '').trim();
    const lastName = String(user?.last_name || '').trim();

    if (!compactNames) {
      return `${firstName} ${lastName}`.trim();
    }

    if (!lastName) return firstName;
    return `${firstName} ${lastName.charAt(0).toUpperCase()}.`.trim();
  };

  const formatStatValue = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
  };

  const getUserRowPath = (userId) =>
    isOwnPlayerPath(userId, viewerUserId) ? buildProfilePath() : buildPlayerPath(userId);

  return (
    <div className="leaderboard-container">
      {/* Search Box */}
      <div className="search-container">
        <input
          type="text"
          placeholder="Search by name..."
          value={searchTerm}
          onChange={handleSearchInput}
          className="search-input"
        />
      </div>

      {/* Leaderboard List */}
      <div className="ranking">
        {isLoading ? (
          <p className="leaderboard-status">Loading leaderboard...</p>
        ) : leaderboardData.length === 0 ? (
          <p className="leaderboard-status">{noResultsMessage}</p>
        ) : (
          leaderboardData.map((user) => (
            <Link
              to={getUserRowPath(user?._id)}
              key={user._id}
              className={`ranking-item ${
                currentUserId != null && String(user._id) === String(currentUserId) ? 'highlight' : ''
              }`}
            >
              {/* 
                We expect the server to provide a 'rank' field 
                that includes the global rank for each user. 
              */}
              <span className="rank">{user.rank}.</span>
              <img
                src={user.profilePictureUrl || profilePic}
                alt={`${user.first_name} ${user.last_name}'s avatar`}
                className="avatar"
              />
              <span className="name">{formatDisplayName(user)}</span>
              <div className="leaderboard-stats" aria-label="Leaderboard stats">
                <span className="leaderboard-stat leaderboard-stat--points">
                  <span className="leaderboard-stat-value">{formatStatValue(user.points)}</span>
                  <span className="leaderboard-stat-label">PTS</span>
                </span>
                <span className="leaderboard-stat">
                  <span className="leaderboard-stat-value">{formatStatValue(user.goals)}</span>
                  <span className="leaderboard-stat-label">G</span>
                </span>
                <span className="leaderboard-stat">
                  <span className="leaderboard-stat-value">{formatStatValue(user.assists)}</span>
                  <span className="leaderboard-stat-label">A</span>
                </span>
              </div>
            </Link>
          ))
        )}
      </div>

      {/* Pagination Controls */}
      {!isLoading && leaderboardData.length > 0 && totalPages > 1 && (
        <div className="pagination-controls">
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <div className="pagination-buttons">
            <Button
              variant="small"
              primaryText="Previous"
              onClick={currentPage === 1 ? undefined : onPrevPage}
              styleType={currentPage === 1 ? 'inactive' : 'active'}
            />
            <Button
              variant="small"
              primaryText="Next"
              onClick={currentPage === totalPages ? undefined : onNextPage}
              styleType={currentPage === totalPages ? 'inactive' : 'active'}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Leaderboard;
